import type { StateStorage } from 'zustand/middleware';
import { fetchUiStateEntries, pushUiStateEntries } from '../../api/grpc/uiStateClient';
import { isGrpcRuntimeAvailable } from '../../api/grpc/grpcRuntime';
import { webLog } from '../../api/grpc/webLogger';
import { getSynchronousLocalStorage } from '../utils/synchronousLocalStorage';
import { LEARNED_AUTO_FILL_LIBRARY_STORAGE_KEY } from '../quickFill/learnedAutoFillLibrary';

/**
 * Durable localStorage mirror.
 *
 * The WebView2 user-data folder — localStorage included — is purged by the
 * plugin's cache-version guard on every plugin version change, and disappears
 * with WebView2 runtime reinstalls. Keys listed in DURABLE_STATE_KEYS are
 * mirrored into a plugin-owned JSON file via UiStateService, and re-hydrated
 * back into localStorage at bootstrap (hydrateDurableState), so the guided
 * workflow's "completed once" markers survive Revit restarts, cache purges,
 * and runtime reinstalls.
 *
 * Write path is always: localStorage first (synchronous, authoritative for
 * the running session), then a fire-and-forget mirror push. Read path is
 * localStorage only — the plugin copy is consulted exactly once per session,
 * during bootstrap hydration.
 */

// The workflow resume store persists under this key; it lives here (not in
// the store module) so the store can import it without creating a cycle.
export const WORKFLOW_RESUME_STORE_STORAGE_KEY = 'pipeex-workflow-resume-store';

// User-project-scoped proof that guided setup reached Mapping Configuration.
export const PROJECT_SETUP_HISTORY_STORAGE_KEY = 'pipeex.userProjectSetupHistory.v1';

// Project ↔ profile binding written by mappingProfileProjectTrust; tells the
// project gate which profile belongs to the active project.
export const PROFILE_TRUST_STORAGE_KEY = 'pipeex.mapping.profileProjectTrust.v1';

// "Active profile" pointer used by restore-on-mount (pcfSettingsActiveProfile).
export const ACTIVE_PROFILE_STORAGE_KEY = 'pipeex.active.pcfSettingsProfile.v1';

// Feature tour / onboarding "seen" flags (and the quick-fill tour resume
// pointer). Owned by the per-feature onboarding modules, which write them via
// setDurableStateValue/clearDurableStateValue; listed here so the flags survive
// WebView2 cache purges and runtime reinstalls instead of re-popping the tour.
export const QUICK_FILL_ONBOARDING_STORAGE_KEY = 'pipeex-feature-onboarding-quick-fill-v1';
export const QUICK_FILL_TOUR_PROGRESS_STORAGE_KEY =
  'pipeex-feature-onboarding-quick-fill-tour-progress-v1';
export const MAPPING_WORKSPACE_ONBOARDING_STORAGE_KEY =
  'pipeex-feature-onboarding-mapping-workspace-v1';
export const VALIDATION_ONBOARDING_STORAGE_KEY = 'pipeex-feature-onboarding-validation-v1';
export const VALIDATION_REPAIR_DETAIL_ONBOARDING_STORAGE_KEY =
  'pipeex-feature-onboarding-validation-repair-detail-v1';
export const VALIDATION_CONDITION_STUDIO_ONBOARDING_STORAGE_KEY =
  'pipeex-feature-onboarding-validation-condition-studio-v1';
export const VALIDATION_CONFIGURATION_ONBOARDING_STORAGE_KEY =
  'pipeex-feature-onboarding-validation-config-v1';
export const EXPORT_ONBOARDING_STORAGE_KEY = 'pipeex-feature-onboarding-export-v1';

export const DURABLE_STATE_KEYS = [
  PROJECT_SETUP_HISTORY_STORAGE_KEY,
  WORKFLOW_RESUME_STORE_STORAGE_KEY,
  PROFILE_TRUST_STORAGE_KEY,
  ACTIVE_PROFILE_STORAGE_KEY,
  LEARNED_AUTO_FILL_LIBRARY_STORAGE_KEY,
  QUICK_FILL_ONBOARDING_STORAGE_KEY,
  QUICK_FILL_TOUR_PROGRESS_STORAGE_KEY,
  MAPPING_WORKSPACE_ONBOARDING_STORAGE_KEY,
  VALIDATION_ONBOARDING_STORAGE_KEY,
  VALIDATION_REPAIR_DETAIL_ONBOARDING_STORAGE_KEY,
  VALIDATION_CONDITION_STUDIO_ONBOARDING_STORAGE_KEY,
  VALIDATION_CONFIGURATION_ONBOARDING_STORAGE_KEY,
  EXPORT_ONBOARDING_STORAGE_KEY,
] as const;

const durableKeySet: ReadonlySet<string> = new Set(DURABLE_STATE_KEYS);

export function isDurableStateKey(key: string): boolean {
  return durableKeySet.has(key);
}

const syncLocalStorage = getSynchronousLocalStorage();
let pendingDurableWrite: Promise<void> | null = null;

function enqueueDurableWrite(write: () => Promise<unknown>): void {
  const currentWrite = pendingDurableWrite === null
    ? write()
    : pendingDurableWrite.then(write, write);
  const settledWrite = currentWrite.then(
    () => undefined,
    () => undefined
  );
  pendingDurableWrite = settledWrite;
  void settledWrite.then(() => {
    if (pendingDurableWrite === settledWrite) {
      pendingDurableWrite = null;
    }
  });
}

// StateStorage.getItem is typed to allow async adapters (Promise returns);
// the synchronous storage used here never returns one.
const readLocalValue = (key: string): string | null =>
  syncLocalStorage.getItem(key) as string | null;

/**
 * Mirror a single durable key to the plugin-side store. Fire-and-forget:
 * failures are logged inside uiStateClient and never surface to the caller.
 */
export function mirrorDurableStateWrite(key: string, value: string | null): void {
  if (!isDurableStateKey(key) || !isGrpcRuntimeAvailable()) {
    return;
  }

  const write = () =>
    value === null
      ? pushUiStateEntries({}, [key])
      : pushUiStateEntries({ [key]: value }, []);
  enqueueDurableWrite(write);
}

/** Waits until every durable write queued before or during the wait settles. */
export async function flushDurableStateWrites(): Promise<void> {
  while (pendingDurableWrite !== null) {
    const pending = pendingDurableWrite;
    await pending;
    if (pendingDurableWrite === pending) {
      return;
    }
  }
}

/**
 * Write a durable key: localStorage synchronously + plugin mirror.
 * Use this instead of raw localStorage writes for DURABLE_STATE_KEYS.
 */
export function setDurableStateValue(key: string, value: string): void {
  syncLocalStorage.setItem(key, value);
  mirrorDurableStateWrite(key, value);
}

/**
 * Remove a durable key: localStorage synchronously + plugin mirror.
 */
export function clearDurableStateValue(key: string): void {
  syncLocalStorage.removeItem(key);
  mirrorDurableStateWrite(key, null);
}

/**
 * Zustand `persist` storage that keeps localStorage synchronous semantics and
 * additionally mirrors every write/remove into the plugin-side store.
 */
export function getDurableStateStorage(): StateStorage {
  return {
    getItem: (name) => syncLocalStorage.getItem(name),
    setItem: (name, value) => {
      syncLocalStorage.setItem(name, value);
      mirrorDurableStateWrite(name, value);
    },
    removeItem: (name) => {
      syncLocalStorage.removeItem(name);
      mirrorDurableStateWrite(name, null);
    },
  };
}

type DurableRehydrator = () => void | Promise<unknown>;

// Zustand stores whose persisted slices must be re-read from localStorage
// after hydration replaced their backing values. Stores register here to keep
// the import graph one-directional (store -> durableState).
const rehydrators: DurableRehydrator[] = [];

export function registerDurableRehydrator(rehydrator: DurableRehydrator): void {
  rehydrators.push(rehydrator);
}

const HYDRATION_TIMEOUT_MS = 2500;

function withTimeout<T>(promise: Promise<T>, fallback: T): Promise<T> {
  return Promise.race([
    promise,
    new Promise<T>((resolve) => setTimeout(() => resolve(fallback), HYDRATION_TIMEOUT_MS)),
  ]);
}

/**
 * Pull the plugin-mirrored values into localStorage. Conflict rules:
 * - plugin has a value -> plugin wins (covers post-purge restores);
 * - plugin has no value but localStorage does -> push the local value up
 *   (covers first run after this shipped, i.e. migration of existing users).
 *
 * Never throws and never blocks startup for longer than HYDRATION_TIMEOUT_MS;
 * any failure leaves localStorage as the source of truth for this session.
 */
export async function hydrateDurableState(): Promise<void> {
  if (!isGrpcRuntimeAvailable()) {
    return;
  }

  const entries = await withTimeout(fetchUiStateEntries([...DURABLE_STATE_KEYS]), null);
  if (entries === null) {
    webLog.warn('durableState', 'hydrate: plugin store unreachable — keeping localStorage state');
    return;
  }

  const migrationPush: Record<string, string> = {};
  for (const key of DURABLE_STATE_KEYS) {
    const pluginValue = entries[key];
    const localValue = readLocalValue(key);

    if (typeof pluginValue === 'string') {
      if (localValue !== pluginValue) {
        syncLocalStorage.setItem(key, pluginValue);
      }
    } else if (localValue !== null) {
      migrationPush[key] = localValue;
    }
  }

  if (Object.keys(migrationPush).length > 0) {
    enqueueDurableWrite(() => pushUiStateEntries(migrationPush, []));
  }

  await Promise.all(
    rehydrators.map(async (rehydrator) => {
      try {
        await rehydrator();
      } catch (error) {
        webLog.warn(
          'durableState',
          `hydrate: rehydrator failed: ${error instanceof Error ? error.message : String(error)}`
        );
      }
    })
  );
}
